// src/pages/orders/WoocommerceSyncPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Download, CalendarDays, Hash, Copy, AlertCircle } from 'lucide-react';
import { PageHeader } from '../../components/common';
import {
  getWoocommerceSyncShops,
  syncWoocommerceOrder,
  syncWoocommerceOrdersByDate,
} from '../../services/api/shops';
import { useAlert } from '../../contexts/AlertContext';
import { useLocalization } from '../../contexts/LocalizationContext';
import { usePermissions } from '../../hooks/usePermissions';
import { formatDateTime } from '../../utils/dateUtils';
import styles from './WoocommerceSyncPage.module.css';

const todayLocal = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const STATUS_CLASS = {
  created: 'statusCreated',
  updated: 'statusUpdated',
  skipped: 'statusSkipped',
  failed: 'statusFailed',
};

const WoocommerceSyncPage = () => {
  const { t } = useLocalization();
  const { success, error, confirm } = useAlert();
  const { hasPermission } = usePermissions();

  const [shops, setShops] = useState([]);
  const [isLoadingShops, setIsLoadingShops] = useState(true);
  const [shopId, setShopId] = useState('');
  const [orderId, setOrderId] = useState('');
  const [date, setDate] = useState(todayLocal());
  const [syncing, setSyncing] = useState(null); // null | 'order' | 'date'
  const [result, setResult] = useState(null);

  const selectedShop = useMemo(
    () => shops.find((s) => String(s.id) === String(shopId)) || null,
    [shops, shopId]
  );
  const canSync = !!selectedShop && selectedShop.hasCredentials && !syncing;

  const loadShops = async () => {
    try {
      setIsLoadingShops(true);
      const data = await getWoocommerceSyncShops();
      const list = data.shops || [];
      setShops(list);
      // Preselect when there is exactly one usable shop
      const usable = list.filter((s) => s.hasCredentials);
      if (usable.length === 1) setShopId(String(usable[0].id));
    } catch (err) {
      error(err.response?.data?.message || t('shops.woocommerceSyncLoadFailed'));
    } finally {
      setIsLoadingShops(false);
    }
  };

  useEffect(() => { loadShops(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runSync = async (mode, fn) => {
    setSyncing(mode);
    setResult(null);
    try {
      const data = await fn();
      setResult({ mode, ...data });
      // refresh "last sync" shown under the selector
      loadShopsSilently();
    } catch (err) {
      error(err.response?.data?.message || t('shops.woocommerceSyncError'));
    } finally {
      setSyncing(null);
    }
  };

  const loadShopsSilently = async () => {
    try {
      const data = await getWoocommerceSyncShops();
      setShops(data.shops || []);
    } catch (_) { /* non-critical */ }
  };

  const handleSyncOrder = (e) => {
    e.preventDefault();
    const id = parseInt(orderId, 10);
    if (!id || id < 1) {
      error(t('shops.woocommerceSyncInvalidOrderId'));
      return;
    }
    runSync('order', () => syncWoocommerceOrder(shopId, id));
  };

  const handleSyncDate = async (e) => {
    e.preventDefault();
    const confirmed = await confirm(
      t('shops.woocommerceSyncConfirm', { shop: selectedShop.name, date }),
      {
        title: t('shops.woocommerceSyncConfirmTitle'),
        confirmText: t('shops.woocommerceSyncOrders'),
        cancelText: t('common.cancel'),
      }
    );
    if (!confirmed) return;
    runSync('date', () => syncWoocommerceOrdersByDate(shopId, date));
  };

  const handleCopyFailed = async () => {
    const ids = (result?.results || [])
      .filter((r) => r.status === 'failed')
      .map((r) => r.wooCommerceOrderId)
      .join(', ');
    try {
      await navigator.clipboard.writeText(ids);
      success(t('shops.woocommerceCopied'), { duration: 2000 });
    } catch (_) {
      error(t('shops.woocommerceCopyFailed'));
    }
  };

  const statusLabel = (status) => ({
    created: t('shops.woocommerceSyncCreated'),
    updated: t('shops.woocommerceSyncUpdated'),
    skipped: t('shops.woocommerceSyncSkipped'),
    failed: t('shops.woocommerceSyncFailed'),
  }[status] || status);

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('shops.woocommerceSyncTitle')}
        description={t('shops.woocommerceSyncDesc')}
        icon={RefreshCw}
      />

      <div className={styles.card}>
        <label htmlFor="wcSyncShop" className={styles.label}>{t('shops.woocommerceSyncSelectShop')}</label>

        {!isLoadingShops && shops.length === 0 ? (
          <div className={styles.notice}>
            <AlertCircle size={18} />
            <span>{t('shops.woocommerceSyncNoShops')}</span>
          </div>
        ) : (
          <select
            id="wcSyncShop"
            className={styles.select}
            value={shopId}
            disabled={isLoadingShops || !!syncing}
            onChange={(e) => { setShopId(e.target.value); setResult(null); }}
          >
            <option value="">{t('shops.woocommerceSyncChooseShop')}</option>
            {shops.map((shop) => (
              <option key={shop.id} value={shop.id} disabled={!shop.hasCredentials}>
                {shop.name} ({shop.orderNumberPrefix}){!shop.hasCredentials ? ' — ' + t('shops.woocommerceSyncMissingCredentials') : ''}
              </option>
            ))}
          </select>
        )}

        {selectedShop && (
          <p className={styles.hint}>
            {selectedShop.lastSyncAt
              ? t('shops.woocommerceSyncLastSync', { date: formatDateTime(selectedShop.lastSyncAt) })
              : t('shops.woocommerceSyncNeverSynced')}
          </p>
        )}

        {shops.some((s) => !s.hasCredentials) && (
          <p className={styles.hint}>
            {t('shops.woocommerceSyncMissingCredentials')}{' '}
            {hasPermission('edit_shop') && (
              <Link
                to={`/shops/${(shops.find((s) => !s.hasCredentials) || {}).id}/edit`}
                className={styles.link}
              >
                {t('shops.woocommerceSyncAddCredentials')}
              </Link>
            )}
          </p>
        )}
      </div>

      <div className={styles.grid}>
        <form className={styles.card} onSubmit={handleSyncOrder}>
          <h2 className={styles.cardTitle}><Hash size={18} />{t('shops.woocommerceSyncByIdTitle')}</h2>
          <p className={styles.hint}>{t('shops.woocommerceSyncByIdDesc')}</p>
          <label htmlFor="wcSyncOrderId" className={styles.label}>{t('shops.woocommerceSyncOrderIdLabel')}</label>
          <input
            id="wcSyncOrderId"
            type="number"
            min="1"
            step="1"
            className={styles.input}
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            disabled={!canSync}
            required
          />
          <button type="submit" className={styles.primaryButton} disabled={!canSync || !orderId}>
            <Download size={16} />
            {syncing === 'order' ? t('shops.woocommerceSyncing') : t('shops.woocommerceSyncFetch')}
          </button>
        </form>

        <form className={styles.card} onSubmit={handleSyncDate}>
          <h2 className={styles.cardTitle}><CalendarDays size={18} />{t('shops.woocommerceSyncByDateTitle')}</h2>
          <p className={styles.hint}>{t('shops.woocommerceSyncByDateDesc')}</p>
          <label htmlFor="wcSyncDate" className={styles.label}>{t('shops.woocommerceSyncDateLabel')}</label>
          <input
            id="wcSyncDate"
            type="date"
            className={styles.input}
            value={date}
            max={todayLocal()}
            onChange={(e) => setDate(e.target.value)}
            disabled={!canSync}
            required
          />
          <button type="submit" className={styles.primaryButton} disabled={!canSync || !date}>
            <RefreshCw size={16} className={syncing === 'date' ? styles.spin : undefined} />
            {syncing === 'date' ? t('shops.woocommerceSyncing') : t('shops.woocommerceSyncOrders')}
          </button>
          {syncing === 'date' && <p className={styles.hint}>{t('shops.woocommerceSyncingHint')}</p>}
        </form>
      </div>

      {result && (
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>{t('shops.woocommerceSyncResults')}</h2>

          <div className={styles.chips}>
            <span className={styles.chip}>{t('shops.woocommerceSyncTotal')}: <strong>{result.summary.total}</strong></span>
            <span className={`${styles.chip} ${styles.statusCreated}`}>{t('shops.woocommerceSyncCreated')}: <strong>{result.summary.created}</strong></span>
            <span className={`${styles.chip} ${styles.statusUpdated}`}>{t('shops.woocommerceSyncUpdated')}: <strong>{result.summary.updated}</strong></span>
            <span className={`${styles.chip} ${styles.statusSkipped}`}>{t('shops.woocommerceSyncSkipped')}: <strong>{result.summary.skipped}</strong></span>
            <span className={`${styles.chip} ${styles.statusFailed}`}>{t('shops.woocommerceSyncFailed')}: <strong>{result.summary.failed}</strong></span>
            {result.summary.failed > 0 && (
              <button type="button" className={styles.secondaryButton} onClick={handleCopyFailed}>
                <Copy size={14} />{t('shops.woocommerceSyncCopyFailed')}
              </button>
            )}
          </div>

          {result.results.length === 0 ? (
            <p className={styles.hint}>{t('shops.woocommerceSyncNoOrders')}</p>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('shops.woocommerceSyncColWcId')}</th>
                    <th>{t('shops.woocommerceSyncColStatus')}</th>
                    <th>{t('shops.woocommerceSyncColOrder')}</th>
                    <th>{t('shops.woocommerceSyncColMessage')}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.results.map((r) => (
                    <tr key={r.wooCommerceOrderId}>
                      <td>#{r.wooCommerceOrderId}</td>
                      <td>
                        <span className={`${styles.badge} ${styles[STATUS_CLASS[r.status]]}`}>
                          {statusLabel(r.status)}
                        </span>
                      </td>
                      <td>
                        {r.orderId ? (
                          <Link to={`/orders/${r.orderId}`} className={styles.link}>{r.orderNumber || r.orderId}</Link>
                        ) : '—'}
                      </td>
                      <td className={styles.message}>{r.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WoocommerceSyncPage;
